import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Dropdown, IDropdownAnchor, isDropdownGroupProps, TDropdownElementProps } from '../Dropdown';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { Intent } from '../utilities';
import { mergeTests } from '../../test-utilities/mergeTests';

const DropdownDiv = styled.div`
  position: relative;
`;

interface IButtonMenuProps extends IHasTestData {
  intent: Intent;
  anchor?: IDropdownAnchor;
  isInline?: boolean;
  items: TDropdownElementProps[];
  zIndex?: number | null;
  disabled?: boolean;
  children?: React.ReactNode;
}

export const ButtonDropdown: React.FC<IButtonMenuProps> = ({
  intent = 'primary',
  isInline,
  children,
  items,
  zIndex,
  anchor,
  'data-test-id': dataTestID,
  disabled,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const closeMenu = () => setShowMenu(false);
  const openMenu = () => setShowMenu(true);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener('click', closeMenu, { once: true, passive: true });
    }
    return () => {
      document.removeEventListener('click', closeMenu);
    };
  }, [showMenu]);

  const dropdownButtons = useMemo(() => {
    return items.map((item) => {
      if ('onClick' in item && item.onClick) {
        return {
          ...item,
          onClick: () => {
            item.onClick?.();
            closeMenu();
          },
        };
      }
      return item;
    });
  }, [items]);

  const onClickMenuButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    //FYI: events inside the dropdown should not escape it
    e.stopPropagation();
    if (dropdownButtons.length) {
      if (dropdownButtons.length === 1) {
        const menuItem = dropdownButtons[0];
        if (isDropdownGroupProps(menuItem)) {
          openMenu();
        } else {
          if (menuItem.onClick) {
            menuItem.onClick(menuItem.href || menuItem.to);
          } else if (menuItem.href) {
            window.location.href = menuItem.href;
          } else if (menuItem.to) {
            window.location.href = menuItem.to;
          }
        }
      } else {
        openMenu();
      }
    }
  };

  return (
    <DropdownDiv>
      <Button
        intent={intent}
        isInline={isInline}
        onClick={onClickMenuButton}
        type="button"
        data-test-id={mergeTests(dataTestID, 'button')}
        disabled={disabled}
      >
        {children || '+ New'}
      </Button>
      {!!dropdownButtons.length && (
        <Dropdown show={showMenu} items={dropdownButtons} zIndex={zIndex} isInline={isInline} anchor={anchor} />
      )}
    </DropdownDiv>
  );
};
